import React, { useState, useEffect } from 'react'
import { Plus, Search, Users, Globe, Crown, UserPlus, Calendar, Lock, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { PageLoader } from '@/components/ui/Loader'
import { Avatar } from '@/components/ui/Avatar'
import { useLeaguesStore } from '@/stores/leaguesStore'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import clsx from 'clsx'

/* ── Frosted glass primitives ───────────────────────────── */
const GlassCard: React.FC<React.HTMLAttributes<HTMLDivElement> & { hover?: boolean }> = ({
  className,
  hover = false,
  ...props
}) => (
  <div
    className={clsx(
      'backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl shadow-lg',
      hover && 'hover:bg-white/[0.07] hover:border-white/[0.14] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 cursor-pointer',
      className,
    )}
    {...props}
  />
)

/* ── League card ────────────────────────────────────────── */
const LeagueCardComponent: React.FC<{
  league: any
  userRank?: number
  topScorer?: any
  onClick: () => void
  isPublic?: boolean
}> = ({ league, userRank, topScorer, onClick, isPublic = false }) => (
  <GlassCard hover onClick={onClick} className="p-5">
    {/* Header row */}
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-white truncate">{league.name}</h3>
        {league.description && (
          <p className="text-sm text-white/40 line-clamp-2 mt-1">{league.description}</p>
        )}
      </div>
      <Badge variant="outline" size="sm" className="ml-2 shrink-0 border-white/10 text-white/50">
        {isPublic ? (
          <><Globe className="h-3 w-3 mr-1" /> Public</>
        ) : (
          <><Lock className="h-3 w-3 mr-1" /> Private</>
        )}
      </Badge>
    </div>

    {/* Meta row */}
    <div className="flex items-center justify-between text-xs text-white/30 mb-4">
      <span className="flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5" />
        {league.memberCount ?? league.currentMembers ?? 0} members
      </span>
      <span className="flex items-center gap-1.5">
        <Calendar className="h-3.5 w-3.5" />
        {league.createdAt ? new Date(league.createdAt as any).toLocaleDateString() : '—'}
      </span>
    </div>

    {/* Prize pool badge */}
    {(league.prizePool > 0 || league.prizeNotes) && (
      <div className="flex items-center gap-1.5 text-xs text-amber-400/70 mb-3">
        <Trophy className="h-3.5 w-3.5" />
        <span>Prize Pool{league.prizePool > 0 ? `: $${league.prizePool}` : ''}</span>
      </div>
    )}

    {/* Your rank */}
    {userRank != null && (
      <div className="flex items-center justify-between p-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl mb-3">
        <span className="text-xs text-white/30">Your Rank</span>
        <Badge variant="primary" size="sm">#{userRank}</Badge>
      </div>
    )}

    {/* Top scorer */}
    {topScorer && (
      <div className="flex items-center gap-2.5">
        <Avatar src={topScorer.avatar} fallback={topScorer.name} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">{topScorer.name}</div>
          <div className="text-[11px] text-white/25">{Number(topScorer.points ?? 0).toLocaleString()} pts</div>
        </div>
      </div>
    )}
  </GlassCard>
)

/* ── Create League form ─────────────────────────────────── */
const CreateLeagueForm: React.FC<{
  onCreate: (data: { name: string; description?: string; maxMembers?: number; privacy?: string }) => Promise<void>
  onCancel: () => void
}> = ({ onCreate, onCancel }) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [privacy, setPrivacy] = useState<'private' | 'public'>('private')
  const [maxMembers, setMaxMembers] = useState<number>(50)
  const [submitting, setSubmitting] = useState(false)

  return (
    <form
      className="space-y-4"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!name.trim()) { toast.error('League name is required'); return }
        setSubmitting(true)
        try {
          await onCreate({ name: name.trim(), description: description.trim() || undefined, privacy, maxMembers: Number.isFinite(maxMembers) ? maxMembers : undefined })
        } catch (err: any) { toast.error(err?.message ?? 'Failed to create league') }
        finally { setSubmitting(false) }
      }}
    >
      <div>
        <label className="block text-xs text-white/40 mb-1">Name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Office Bracket War" className="bg-white/[0.04] border-white/[0.08]" />
      </div>
      <div>
        <label className="block text-xs text-white/40 mb-1">Description (optional)</label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's the vibe?" className="bg-white/[0.04] border-white/[0.08]" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-white/40 mb-1">Privacy</label>
          <select
            value={privacy}
            onChange={(e) => setPrivacy(e.target.value as any)}
            className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="private">Private (invite code)</option>
            <option value="public">Public</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-white/40 mb-1">Max members</label>
          <Input type="number" min={2} max={1000} value={String(maxMembers)} onChange={(e) => setMaxMembers(parseInt(e.target.value || '0', 10))} className="bg-white/[0.04] border-white/[0.08]" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={submitting}>{submitting ? 'Creating…' : 'Create League'}</Button>
      </div>
    </form>
  )
}

/* ── Join League form ───────────────────────────────────── */
const JoinLeagueForm: React.FC<{
  onJoin: (inviteCode: string) => Promise<void>
  onCancel: () => void
}> = ({ onJoin, onCancel }) => {
  const [inviteCode, setInviteCode] = useState('')
  const [submitting, setSubmitting] = useState(false)

  return (
    <form
      className="space-y-4"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!inviteCode.trim()) { toast.error('Invite code is required'); return }
        setSubmitting(true)
        try { await onJoin(inviteCode.trim()) } catch (err: any) { toast.error(err?.message ?? 'Failed to join league') }
        finally { setSubmitting(false) }
      }}
    >
      <div>
        <label className="block text-xs text-white/40 mb-1">Invite code</label>
        <Input value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="e.g. BBAB12CD" className="bg-white/[0.04] border-white/[0.08]" />
        <p className="text-[11px] text-white/20 mt-2">Codes are case-insensitive.</p>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={submitting}>{submitting ? 'Joining…' : 'Join'}</Button>
      </div>
    </form>
  )
}

/* ── Main page ──────────────────────────────────────────── */
const LeaguesPage: React.FC = () => {
  const navigate = useNavigate()
  const {
    leagues, publicLeagues, isLoading, error,
    fetchLeagues, fetchPublicLeagues, createLeague, joinLeague, joinLeagueByCode,
  } = useLeaguesStore()

  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<'my-leagues' | 'public'>('my-leagues')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showJoinModal, setShowJoinModal] = useState(false)

  useEffect(() => { fetchLeagues() }, [fetchLeagues])
  useEffect(() => { if (activeTab === 'public') fetchPublicLeagues() }, [activeTab, fetchPublicLeagues])

  const filteredMyLeagues = leagues.filter(league =>
    league.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    league.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )
  const filteredPublicLeagues = publicLeagues.filter((league: any) =>
    league.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    league.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleLeagueClick = (leagueId: string) => navigate(`/leagues/${leagueId}`)

  const handleJoinPublicLeague = async (leagueId: string) => {
    try {
      await joinLeague(leagueId)
      await fetchLeagues()
      toast.success('Joined league')
      navigate(`/leagues/${leagueId}`)
    } catch (error: any) {
      toast.error(error?.message ?? 'This league requires an invite code')
    }
  }

  if (isLoading && leagues.length === 0) return <PageLoader text="Loading leagues..." />

  return (
    <>
      <div className="min-h-screen bg-transparent px-4 py-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-gradient-to-br from-blue-500/80 to-purple-500/80 rounded-xl backdrop-blur">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white">Leagues</h1>
                <p className="text-white/30 text-sm">Compete with friends and join communities</p>
              </div>
            </div>
          </div>

          {/* Search + Actions */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Search leagues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
                className="bg-white/[0.04] border-white/[0.08]"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="primary" onClick={() => setShowCreateModal(true)} leftIcon={<Plus className="h-4 w-4" />} size="lg">
                Create League
              </Button>
              <Button variant="outline" onClick={() => setShowJoinModal(true)} leftIcon={<UserPlus className="h-4 w-4" />} size="lg">
                Join with Code
              </Button>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex justify-center mb-8">
            <div className="backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] p-1 rounded-xl inline-flex">
              <button
                onClick={() => setActiveTab('my-leagues')}
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5',
                  activeTab === 'my-leagues' ? 'bg-white/[0.1] text-white' : 'text-white/40 hover:text-white/60',
                )}
              >
                <Crown className="h-4 w-4" /> My Leagues
              </button>
              <button
                onClick={() => setActiveTab('public')}
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5',
                  activeTab === 'public' ? 'bg-white/[0.1] text-white' : 'text-white/40 hover:text-white/60',
                )}
              >
                <Globe className="h-4 w-4" /> Discover
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <GlassCard className="p-4 mb-6 border-red-500/20">
              <div className="text-red-400 font-medium text-sm">Error loading leagues</div>
              <div className="text-red-300/60 text-xs mt-1">{error}</div>
              <Button variant="outline" size="sm" onClick={fetchLeagues} className="mt-2 border-red-500/20 text-red-400 hover:bg-red-500/10">
                Try Again
              </Button>
            </GlassCard>
          )}

          {/* Content */}
          {activeTab === 'my-leagues' && (
            <>
              {filteredMyLeagues.length === 0 && !isLoading ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-full flex items-center justify-center mx-auto mb-5">
                    <Users className="h-10 w-10 text-white/20" />
                  </div>
                  {searchTerm ? (
                    <>
                      <h3 className="text-lg font-semibold text-white mb-2">No leagues found</h3>
                      <p className="text-white/30 text-sm max-w-md mx-auto mb-4">No leagues match "{searchTerm}".</p>
                      <Button variant="outline" onClick={() => setSearchTerm('')}>Clear Search</Button>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-white mb-2">No leagues yet</h3>
                      <p className="text-white/30 text-sm max-w-md mx-auto mb-6">Create your own league or join an existing one to start competing!</p>
                      <div className="flex gap-3 justify-center">
                        <Button variant="primary" onClick={() => setShowCreateModal(true)} leftIcon={<Plus className="h-4 w-4" />}>Create League</Button>
                        <Button variant="outline" onClick={() => setActiveTab('public')} leftIcon={<Globe className="h-4 w-4" />}>Browse Public</Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {filteredMyLeagues.map((league: any) => {
                    const userRank = league.myRank ?? league.yourRank ?? null
                    const topScorer = league.topPlayer
                      ? { name: league.topPlayer.displayName || league.topPlayer.username || 'Leader', avatar: league.topPlayer.avatarUrl, points: league.topPlayer.score ?? 0 }
                      : null
                    return (
                      <LeagueCardComponent key={league.id} league={league} userRank={userRank ?? undefined} topScorer={topScorer ?? undefined} onClick={() => handleLeagueClick(league.id)} isPublic={league.privacy === 'public'} />
                    )
                  })}
                </div>
              )}
            </>
          )}

          {activeTab === 'public' && (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-1">Discover Leagues</h3>
                <p className="text-white/30 text-sm">Browse public leagues. Private leagues require an invite code.</p>
              </div>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {filteredPublicLeagues.map((league: any) => {
                  const topScorer = league.topPlayer
                    ? { name: league.topPlayer.displayName || league.topPlayer.username || 'Leader', avatar: league.topPlayer.avatarUrl, points: league.topPlayer.score ?? 0 }
                    : null
                  return (
                    <LeagueCardComponent key={league.id} league={league} topScorer={topScorer ?? undefined} onClick={() => handleJoinPublicLeague(league.id)} isPublic={league.privacy === 'public'} />
                  )
                })}
              </div>
              <div className="text-center mt-8">
                <p className="text-white/20 text-xs">
                  Click to join instantly, or{' '}
                  <button onClick={() => setShowJoinModal(true)} className="text-orange-400 hover:text-orange-300 underline">use an invite code</button>
                  {' '}for private leagues.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <GlassCard className="max-w-md w-full p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Create League</h3>
                <p className="text-white/30 text-xs mt-1">Start a private league and invite friends.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>Close</Button>
            </div>
            <CreateLeagueForm
              onCancel={() => setShowCreateModal(false)}
              onCreate={async (data) => {
                const id = await createLeague(data)
                toast.success('League created')
                setShowCreateModal(false)
                navigate(`/leagues/${id}`)
              }}
            />
          </GlassCard>
        </div>
      )}

      {/* Join modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <GlassCard className="max-w-md w-full p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Join League</h3>
                <p className="text-white/30 text-xs mt-1">Enter an invite code to join a private league.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowJoinModal(false)}>Close</Button>
            </div>
            <JoinLeagueForm
              onCancel={() => setShowJoinModal(false)}
              onJoin={async (inviteCode) => {
                await joinLeagueByCode(inviteCode)
                toast.success('Joined league')
                setShowJoinModal(false)
                await fetchLeagues()
                setActiveTab('my-leagues')
              }}
            />
          </GlassCard>
        </div>
      )}
    </>
  )
}

export { LeaguesPage }
